/**
 * PlatformConnectionCard — carte d'une plateforme de vente (S6 PR1 + PR5).
 *
 * Quatre états visuels selon (`is_implemented`, `connection`,
 * `connection.revoked_at`) :
 *
 *   1. **disabled** : `is_implemented = false` (Pinterest/Insta/TikTok/…
 *      pas encore livrés). Carte grisée + Badge "Bientôt disponible". Pas
 *      de bouton de connexion (un click renverrait 500 côté BE faute
 *      d'adapter câblé).
 *
 *   2. **available** : implémentée mais pas connectée. Bouton "Connecter
 *      <Plateforme>" qui appelle `useStartEtsyOAuth` puis
 *      `window.location.href = url` (redirection full page vers Etsy
 *      `/oauth/connect`). Le user revient via `/api/auth/etsy/callback`
 *      qui le redirige sur `/settings?tab=connections&etsy=ok|denied|failed`.
 *
 *   3. **connected** : connexion existante valide. Affiche `display_name`
 *      + "Lien actif" basé sur `refresh_token_renewed_at` + scopes +
 *      bouton "Déconnecter" (via `useConfirm` danger). On N'AFFICHE PAS
 *      `expires_at` (= access_token, 1h Etsy, périme par design — afficher
 *      cette date génèrerait une fausse alerte horaire pour l'utilisatrice ;
 *      cf. design 2026-05-10).
 *
 *   4. **revoked** : `connection.revoked_at` non null. La plateforme a
 *      refusé le refresh_token (token révoqué côté boutique, app
 *      suspendue, scope retiré). Badge danger "Reconnexion nécessaire" +
 *      bouton "Reconnecter" qui ré-emprunte le flow OAuth (idempotent —
 *      `upsert_connection` reset `revoked_at = NULL` si succès).
 *
 * Différence avec `<PodConnectionCard>` (S5.6) : POD utilise une API key
 * en formulaire RHF, plateformes utilisent OAuth full-page redirect. Pas
 * de form inline ici — la sécurité OAuth interdit que le secret transite
 * par notre app, c'est Etsy qui demande consent à Stéphanie.
 *
 * Verrous tenus :
 *   - Pas de `<button>` natif (utilise `<Button>`).
 *   - Pas de `window.confirm` (utilise `useConfirm`).
 *   - Pas de hex direct (tokens `--color-*` consommés via classes).
 */

import { AlertTriangle, Loader2, LogIn, RotateCw, Unplug } from 'lucide-react'
import { toast } from 'sonner'

import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import { useConfirm } from '@/contexts/confirm-dialog-context'
import {
  useDisconnectPlatform,
  useStartEtsyOAuth,
  useStartPinterestOAuth,
} from '@/hooks/usePlatformConnections'
import {
  PLATFORM_LABEL_FR,
  PLATFORM_TAGLINE_FR,
  type Platform,
  type PlatformConnectionResponse,
} from '@/types/platform-connections'

import PinterestBoardsChips from './PinterestBoardsChips'
import PinterestConnectionExtras from './PinterestConnectionExtras'
import './PlatformConnectionCard.css'

interface PlatformConnectionCardProps {
  platform: Platform
  isImplemented: boolean
  connection: PlatformConnectionResponse | null
}

export default function PlatformConnectionCard({
  platform,
  isImplemented,
  connection,
}: PlatformConnectionCardProps) {
  const { confirm } = useConfirm()
  const disconnectMutation = useDisconnectPlatform()
  const startEtsyMutation = useStartEtsyOAuth()
  const startPinterestMutation = useStartPinterestOAuth()

  const label = PLATFORM_LABEL_FR[platform]
  const tagline = PLATFORM_TAGLINE_FR[platform]

  const isRevoked = Boolean(connection?.revoked_at)
  const dataState = !isImplemented
    ? 'disabled'
    : isRevoked
      ? 'revoked'
      : connection
        ? 'connected'
        : 'available'

  // Dispatch OAuth par plateforme. Pattern extensible — chaque plateforme
  // ajoute son hook + son entrée ici. La pending mutualisée (`isStarting`)
  // pilote le spinner du bouton sans qu'il y ait de couplage entre les
  // mutations.
  const isStarting =
    platform === 'etsy' ? startEtsyMutation.isPending : startPinterestMutation.isPending

  const handleConnect = async () => {
    try {
      let authorizeUrl: string
      if (platform === 'etsy') {
        const { authorize_url } = await startEtsyMutation.mutateAsync()
        authorizeUrl = authorize_url
      } else if (platform === 'pinterest') {
        const { authorize_url } = await startPinterestMutation.mutateAsync()
        authorizeUrl = authorize_url
      } else {
        // Insta/TikTok/Saatchi/Artfinder pas encore livrés. La carte est
        // grisée (`isImplemented=false`) donc le bouton n'est même pas
        // rendu, mais on garde ce filet pour un futur ajout d'enum sans
        // mise à jour de PLATFORM_IS_IMPLEMENTED.
        toast.error(`OAuth ${label} pas encore livré.`)
        return
      }
      // Full-page redirect — on quitte volontairement /settings, le
      // callback nous ramène sur /settings?tab=connections&{platform}=ok.
      window.location.href = authorizeUrl
    } catch (err) {
      const detail =
        err && typeof err === 'object' && 'detail' in err
          ? String((err as { detail: unknown }).detail)
          : `Connexion ${label} impossible.`
      toast.error(detail)
    }
  }

  const handleDisconnect = async () => {
    if (!connection) return
    const ok = await confirm({
      title: `Déconnecter ${label} ?`,
      message:
        'Les publications déjà en ligne sur la plateforme restent en place. ' +
        'Pour publier ou éditer de nouvelles œuvres, il faudra reconnecter ce compte.',
      variant: 'danger',
      confirmText: 'Déconnecter',
    })
    if (!ok) return
    try {
      await disconnectMutation.mutateAsync(connection.id)
      toast.success(`${label} déconnecté.`)
    } catch (err) {
      const detail =
        err && typeof err === 'object' && 'detail' in err
          ? String((err as { detail: unknown }).detail)
          : 'Déconnexion impossible.'
      toast.error(detail)
    }
  }

  return (
    <article
      className="platform-connection-card"
      data-state={dataState}
      aria-labelledby={`platform-connection-${platform}-title`}
    >
      <header className="platform-connection-card-header">
        <div>
          <h3
            id={`platform-connection-${platform}-title`}
            className="ui-h4 platform-connection-card-title"
          >
            {label}
          </h3>
          <p className="ui-body ui-text-muted platform-connection-card-subtitle">{tagline}</p>
        </div>
        {!isImplemented ? (
          <Badge variant="warning" size="sm" title="Adapter pas encore livré côté code.">
            Bientôt disponible
          </Badge>
        ) : isRevoked ? (
          <Badge variant="danger" size="sm" dot>
            Reconnexion nécessaire
          </Badge>
        ) : connection ? (
          <Badge variant="success" size="sm" dot>
            Connecté
          </Badge>
        ) : (
          <Badge variant="default" size="sm">
            Non connecté
          </Badge>
        )}
      </header>

      <div className="platform-connection-card-body">
        {!isImplemented ? (
          <p className="ui-body ui-text-muted">
            L’adapter <strong>{label}</strong> sera livré dans un sprint ultérieur. Connecte
            <strong> Etsy</strong> pour démarrer en attendant.
          </p>
        ) : isRevoked && connection ? (
          <RevokedView
            label={label}
            connection={connection}
            disabled={isStarting || disconnectMutation.isPending}
            onReconnect={handleConnect}
            onDisconnect={handleDisconnect}
          />
        ) : connection ? (
          <ConnectedView
            platform={platform}
            label={label}
            connection={connection}
            disabled={disconnectMutation.isPending}
            onDisconnect={handleDisconnect}
          />
        ) : (
          <AvailableView label={label} disabled={isStarting} onConnect={handleConnect} />
        )}
      </div>
    </article>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Sous-vues
// ──────────────────────────────────────────────────────────────────────────────

interface AvailableViewProps {
  label: string
  disabled: boolean
  onConnect: () => void
}

function AvailableView({ label, disabled, onConnect }: AvailableViewProps) {
  return (
    <>
      <p className="ui-body ui-text-muted">
        Tu vas être redirigé vers <strong>{label}</strong> pour autoriser l’atelier à publier en ton
        nom. Le token retourné est chiffré avant stockage. Tu pourras le révoquer à tout moment
        depuis cette page ou côté {label}.
      </p>
      <div className="platform-connection-card-actions">
        <Button type="button" variant="primary" size="md" onClick={onConnect} disabled={disabled}>
          {disabled ? (
            <Loader2 aria-hidden="true" size={16} className="platform-spinner" />
          ) : (
            <LogIn aria-hidden="true" size={16} />
          )}
          Connecter {label}
        </Button>
      </div>
    </>
  )
}

interface ConnectedViewProps {
  platform: Platform
  label: string
  connection: PlatformConnectionResponse
  disabled: boolean
  onDisconnect: () => void
}

function ConnectedView({
  platform,
  label,
  connection,
  disabled,
  onDisconnect,
}: ConnectedViewProps) {
  return (
    <>
      <dl className="platform-connection-card-meta">
        <dt>Compte</dt>
        <dd>{connection.display_name ?? `Compte #${connection.external_account_id}`}</dd>

        <dt>Connecté le</dt>
        <dd>{new Date(connection.created_at).toLocaleDateString('fr-FR')}</dd>

        {connection.refresh_token_renewed_at ? (
          <>
            <dt>Lien renouvelé</dt>
            <dd>{new Date(connection.refresh_token_renewed_at).toLocaleString('fr-FR')}</dd>
          </>
        ) : null}

        {connection.scopes && connection.scopes.length > 0 ? (
          <>
            <dt>Permissions</dt>
            <dd>{connection.scopes.join(' · ')}</dd>
          </>
        ) : null}

        {connection.last_sync_at ? (
          <>
            <dt>Dernière sync.</dt>
            <dd>
              {new Date(connection.last_sync_at).toLocaleString('fr-FR')}
              {connection.last_sync_status ? ` — ${connection.last_sync_status}` : null}
            </dd>
          </>
        ) : null}
      </dl>

      {platform === 'pinterest' ? (
        <>
          <PinterestBoardsChips connectionId={connection.id} />
          <PinterestConnectionExtras
            connectionId={connection.id}
            isStandard={connection.pinterest?.is_standard ?? false}
          />
        </>
      ) : null}

      <div className="platform-connection-card-actions">
        <Button variant="danger" size="md" onClick={onDisconnect} disabled={disabled}>
          {disabled ? (
            <Loader2 aria-hidden="true" size={16} className="platform-spinner" />
          ) : (
            <Unplug aria-hidden="true" size={16} />
          )}
          Déconnecter {label}
        </Button>
      </div>
    </>
  )
}

interface RevokedViewProps {
  label: string
  connection: PlatformConnectionResponse
  disabled: boolean
  onReconnect: () => void
  onDisconnect: () => void
}

function RevokedView({ label, connection, disabled, onReconnect, onDisconnect }: RevokedViewProps) {
  return (
    <>
      <p className="ui-body platform-connection-card-revoked-explainer">
        <AlertTriangle aria-hidden="true" size={16} />
        <span>
          La plateforme a refusé le dernier renouvellement du jeton OAuth de{' '}
          <strong>{connection.display_name ?? `Compte #${connection.external_account_id}`}</strong>.
          Cela arrive quand le compte révoque l’accès, change de mot de passe, ou que l’app est
          suspendue. Re-connecter <strong>{label}</strong> reposera un nouveau jeton (le compte côté
          plateforme reste le même).
        </span>
      </p>

      <dl className="platform-connection-card-meta">
        <dt>Compte</dt>
        <dd>{connection.display_name ?? `Compte #${connection.external_account_id}`}</dd>
        {connection.revoked_at ? (
          <>
            <dt>Révoquée le</dt>
            <dd>{new Date(connection.revoked_at).toLocaleString('fr-FR')}</dd>
          </>
        ) : null}
      </dl>

      <div className="platform-connection-card-actions">
        <Button variant="primary" size="md" onClick={onReconnect} disabled={disabled}>
          {disabled ? (
            <Loader2 aria-hidden="true" size={16} className="platform-spinner" />
          ) : (
            <RotateCw aria-hidden="true" size={16} />
          )}
          Reconnecter {label}
        </Button>
        <Button variant="ghost" size="md" onClick={onDisconnect} disabled={disabled}>
          <Unplug aria-hidden="true" size={16} />
          Supprimer
        </Button>
      </div>
    </>
  )
}
